import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Chart } from 'chart.js';
import { ChartType } from '../core/chart.model';
import { UserType } from '../core/userType.model';
import { AuthService } from '../core/auth/auth.service';
import { AppConfig } from 'src/config/app.config';

@Component({
  selector: 'app-commission-invoice-dashboard',
  templateUrl: './commission-invoice-dashboard.component.html',
  styleUrls: ['./commission-invoice-dashboard.component.css']
})
export class CommissionInvoiceDashboardComponent implements OnInit, AfterViewInit {

  private _apiUrl: string;
  isAdminView: boolean = false;
  userBaseTypeId: number;

  stats: any = null;
  claimStats: any = null;
  private _claimRawData: any = null;
  chartType: ChartType = ChartType.bar;
  statusChart: any;
  trendChart: any;
  claimChart: any;

  constructor(
    private authService: AuthService,
    private appConfig: AppConfig,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this._apiUrl = appConfig.baseUrl;
    this.userBaseTypeId = parseInt(atob(localStorage.getItem('USER_BASE_TYPE_ID')));
    this.isAdminView = [
      UserType.SysAdmin,
      UserType.Admin,
      UserType.Finance,
      UserType.AzPAdmin
    ].includes(this.userBaseTypeId);
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
     this.getDashboard(); 
    this.getClaimDashboard();
  }

  getDashboard() {
    const endpoint = this.isAdminView
      ? '/api/v1/Dashboard/dealercommission/admin'
      : '/api/v1/Dashboard/dealercommission/dealer';

    this.authService.get(this._apiUrl + endpoint).subscribe(
      (response) => {
        if (response) {
          this.stats = response;
          this.cdr.detectChanges();
          this.buildStatusChart(response);
          this.buildTrendChart(response);
        }
      },
      (error) => console.error(error)
    );
  }

  getClaimDashboard() {
    this.authService.get(this._apiUrl + '/api/v1/Dashboard/dealerClaimChart').subscribe(
      (response) => {
        if (response) {
          this._claimRawData = response;
          this.claimStats = {
            underProcess: response.data[0],
            paid: response.data[1],
            rejected: response.data[2],
            total: response.data[0] + response.data[1] + response.data[2]
          };
          this.cdr.detectChanges();
          this.buildClaimChart(response);
        }
      },
      (error) => console.error(error)
    );
  }

  buildClaimChart(data: any) {
    if (this.claimChart) this.claimChart.destroy();

    this.claimChart = new Chart('claimStatusChart', {
      type: 'bar',
      data: {
        labels: data.label,
        datasets: [{
          data: data.data,
          backgroundColor: [
            'rgba(247, 178, 0, 0.75)',
            'rgba(96, 153, 49, 0.75)',
            'rgba(208, 2, 27, 0.75)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        legend: { display: false },
        scales: {
          xAxes: [{ display: true }],
          yAxes: [{ display: true, ticks: { beginAtZero: true, stepSize: 1 } }]
        },
        onClick: (_evt, activeElements: any[]) => {
          if (activeElements.length > 0) {
            this.router.navigate(['/dealerclaim'], { queryParams: { status: activeElements[0]._index } });
          }
        }
      }
    });
  }

  navigateToClaim(status: number) {
    this.router.navigate(['/dealerclaim'], { queryParams: { status } });
  }

  buildStatusChart(data: any) {
    const labels = data.statusChart?.label ?? ['Submitted', 'Invoice Accepted', 'Invoice Rejected', 'Invoice Under Process', 'Invoice Paid'];
    const counts = data.statusChart?.data ?? [data.submitted, data.invoiceAccepted, data.invoiceRejected, data.invoiceUnderProcess, data.invoicePaid];
    const colors = [
      'rgba(79, 156, 242, 0.75)',   // Submitted — blue
      'rgba(96, 153, 49, 0.75)',    // Invoice Accepted — green
      'rgba(208, 2, 27, 0.75)',     // Invoice Rejected — red
      'rgba(239, 127, 0, 0.75)',    // Invoice Under Process — amber
      'rgba(0, 153, 120, 0.75)'     // Invoice Paid — teal
    ];

    if (this.statusChart) this.statusChart.destroy();

    this.statusChart = new Chart('commStatusChart', {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          data: counts,
          backgroundColor: colors,
          borderColor: colors.map(c => c.replace('0.75', '1')),
          borderWidth: 1
        }]
      },
      options: {
        legend: { display: false },
        scales: {
          xAxes: [{ display: true }],
          yAxes: [{ display: true, ticks: { beginAtZero: true, stepSize: 1 } }]
        },
        onClick: (_evt, activeElements: any[]) => {
          if (activeElements.length > 0) {
            const idx = activeElements[0]._index;
            this.navigateTo(labels[idx]);
          }
        }
      }
    });
  }

  buildTrendChart(data: any) {
    const labels = data.trendChart?.label ?? [];
    const counts = data.trendChart?.data  ?? [];

    if (this.trendChart) this.trendChart.destroy();

    this.trendChart = new Chart('commTrendChart', {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Invoices Raised',
          data: counts,
          backgroundColor: 'rgba(79, 156, 242, 0.6)',
          borderColor: 'rgba(79, 156, 242, 1)',
          borderWidth: 1
        }]
      },
      options: {
        legend: { display: false },
        scales: {
          xAxes: [{ display: true }],
          yAxes: [{ display: true, ticks: { beginAtZero: true, stepSize: 1 } }]
        }
      }
    });
  }

  onTabChange(event: any) {
    const idx = event.index ?? event;
    if (idx === 0 && this.stats) {
      setTimeout(() => {
        this.buildStatusChart(this.stats);
        this.buildTrendChart(this.stats);
      }, 50);
    } else if (idx === 1 && this._claimRawData) {
      setTimeout(() => this.buildClaimChart(this._claimRawData), 50);
    }
  }

  changeChartType(newType: ChartType) {
    if (this.statusChart) {
      this.statusChart.config.type = ChartType[newType];
      this.statusChart.update();
    }
  }

  navigateTo(status: string) {
    if (this.isAdminView) {
      this.router.navigate(['/unauthorized/commissioninvoices'], { queryParams: { status } });
    } else {
      this.router.navigate(['/commissioninvoice'], { queryParams: { status } });
    }
  }

  ageingBadge(bucket: string): string {
    switch (bucket) {
      case 'underThirty': return 'positive';
      case 'thirtyToSixty': return 'active';
      case 'sixtyToNinety': return 'active';
      case 'ninetyToOneTwenty': return 'critical';
      case 'overOneTwenty': return 'critical';
      default: return 'active';
    }
  }

  ageingClass(bucket: string): string {
    switch (bucket) {
      case 'underThirty': return 'ageing-fresh';
      case 'thirtyToSixty': return 'ageing-warn';
      case 'sixtyToNinety': return 'ageing-alert';
      case 'ninetyToOneTwenty': return 'ageing-critical';
      case 'overOneTwenty': return 'ageing-critical';
      default: return '';
    }
  }
}
